import { buildScriptContext } from './ctx';
import { matchUrlPattern } from './urlPattern';

/**
 * Client-side Custom Scripts engine (dashboard entrypoint, Stories 9.3 + 9.4).
 *
 * Reads the server-rendered JSON island `#cw-custom-scripts` (delivered by 9.2 via
 * layouts/_custom_scripts.html.erb), runs each `javascript` script as the body of an
 * isolated `async (ctx) => {}` sandbox, injects `css` as a managed <style> tag, and
 * exposes the `ctx` SDK with safe DOM helpers + teardown.
 *
 * Lifecycle (9.4): each script has a per-`id` live instance in `instances`. `run_on:
 * load` scripts run ONCE at boot (if `url_pattern` matches the boot path) and persist
 * until `teardown()`. `run_on: each_route` scripts re-run on every `router.afterEach`,
 * tearing down their prior instance first (id-keyed registry invariant → no duplicate
 * DOM / leaked listeners, EC1/EC2). `teardown()` also unregisters the `afterEach` hook
 * (AC7) so a post-teardown navigation cannot re-run scripts on a dead engine.
 */

// Single eval chokepoint (EC19). NOTE: `new Function`/`AsyncFunction`/`eval` are NOT
// authorized by a CSP nonce — only inline <script> tags are. If CSP is ever enabled,
// `script-src` MUST include `unsafe-eval`. Keep the eval in this one place.
const AsyncFunction = Object.getPrototypeOf(async () => {}).constructor;

const ISLAND_ID = 'cw-custom-scripts';
const CSS_ATTR = 'data-cw-script';

// Run-reporter defaults (9.5). Batched outcomes are flushed to the public /runs endpoint on
// a short debounce timer and on pagehide, via plain `fetch` (surface-agnostic for 9.6).
const RUNS_URL = '/custom_scripts/runs';
const FLUSH_DELAY_MS = 3000;
// Mirror of CustomScript::LAST_RUN_ERROR_LIMIT — the server truncates authoritatively (EC13),
// this just keeps the wire payload small.
const ERROR_MESSAGE_LIMIT = 1000;
// Mirror of CustomScripts::RunsController::MAX_RUNS_PER_REQUEST — the server drops elements
// past its cap, so oversized queues must be sent in order as sequential chunks (REL-002),
// otherwise the newest outcomes (exactly the ones dedup won't re-send) would be lost.
const RUNS_BATCH_LIMIT = 50;
// Retry ceiling for exponential backoff with full jitter (REL-002): a failed flush retries at
// random(0, min(CAP, delay × 2^failures)) so recovering fleets of tabs don't stampede the
// server in sync; the counter resets on the first confirmed flush.
const FLUSH_BACKOFF_CAP_MS = 60000;
// Per-request body cap (REL-KEEPALIVE-001). `fetch(keepalive:true)` rejects when a request body
// exceeds the browser's ~64KB keepalive budget; a 50-count chunk of `error` runs with multi-byte
// (pt-BR) messages can blow past that. So each request is bounded by BYTES too, not just count —
// which is what actually prevents the size rejection (kept below 64KB with margin).
const MAX_BODY_BYTES = 56 * 1024;
// Aggregate keepalive budget for the terminal (unload) drain: the browser caps the SUM of all
// in-flight keepalive bodies at ~64KB, and the terminal drain fires several POSTs at once. Send
// chunks until this budget is spent; a queue larger than this on a truly-dying page loses its tail
// (unavoidable browser limit — self-heals on the next session's fresh report).
const TERMINAL_KEEPALIVE_BUDGET_BYTES = 60 * 1024;
// Defensive upper bound on the queue (REL-QUEUE-GROWTH-006): against a long-down endpoint on a
// long-lived tab, drop the OLDEST outcome past this and roll back its dedup entry so the status is
// re-recorded later. Generous — status-change dedup already bounds normal growth well below this.
const MAX_QUEUE = 500;

const defaultLogger = {
  // eslint-disable-next-line no-console
  log: (...args) => console.log('[CustomScripts]', ...args),
  // eslint-disable-next-line no-console
  error: (...args) => console.error('[CustomScripts]', ...args),
};

// Reads and parses the JSON island. Rails `json_escape` (9.2) emits JSON unicode
// escapes (e.g. `<`) which `JSON.parse` decodes natively — no HTML-entity
// decoding is needed here. Returns [] on missing island or malformed JSON.
export function readIsland(doc = document, logger = defaultLogger) {
  const el = doc.getElementById(ISLAND_ID);
  try {
    return JSON.parse(el?.textContent || '[]');
  } catch (error) {
    logger.error('failed to parse custom scripts island', error);
    return [];
  }
}

// --- Run-reporter (9.5, hardened 9.9/REL-002, document-scoped 9.9/REL-003) --------------
// Structured run outcomes queued for the /runs endpoint. Reports only on status CHANGE per id
// (a script erroring every route does not re-POST the same error, AC3a), batches into
// ≤RUNS_BATCH_LIMIT POSTs, and flushes on a debounce timer + pagehide (keepalive).
//
// Delivery is SERIALIZED (one in-flight POST at a time, REL-002): batches depart in queue order
// and a failed batch is re-queued at the FRONT, so a stale outcome can never land after — and
// overwrite — a newer confirmed status (the server applies writes last-wins by id).
//
// REL-003: the reporter's correct lifetime is the DOCUMENT, not an engine instance. The portal
// tears down and recreates the engine on every Turbolinks navigation; a per-instance reporter
// let a dead engine's retry/in-flight POST overwrite a newer engine's status (cross-instance
// stale write). One reporter shared across every engine on the page keeps ALL outcomes in a
// single serialized queue with a single dedup map, so ordering holds across engine instances
// by construction. It owns its own pagehide/visibilitychange listeners (document lifecycle) and
// is torn down only on real document unload — never on an engine teardown.
export function createRunReporter({
  fetchFn = typeof window !== 'undefined' && window.fetch
    ? window.fetch.bind(window)
    : null,
  runsUrl = RUNS_URL,
  flushDelay = FLUSH_DELAY_MS,
} = {}) {
  const runQueue = [];
  const lastStatus = new Map(); // id → last reported 'ok' | 'error'
  let flushTimer = null;
  let inFlight = false; // single-flight lock for the normal (debounced) send
  let flushFailures = 0; // consecutive unconfirmed flushes, drives the retry backoff

  // Byte length of a UTF-8 body — String.length counts UTF-16 units, not bytes, so multi-byte
  // (pt-BR / emoji) would under-count against the keepalive limit (REL-KEEPALIVE-001).
  const textEncoder =
    typeof TextEncoder !== 'undefined' ? new TextEncoder() : null;
  const byteLen = str =>
    textEncoder ? textEncoder.encode(str).length : str.length;

  // Convergence invariant (REL-005b): the server must end at the LAST recorded status per id.
  // `lastStatus` is the single source of truth for that latest status, so a queued run whose
  // status has since been superseded by a newer `record()` yields `isFresh === false`. We apply
  // this ONLY when RE-QUEUING a failed batch (see `settle`): a stale outcome re-inserted at the
  // queue FRONT could otherwise land — and regress the server — AFTER a newer status that a
  // terminal drain already sent (the retry × terminal-drain race, REL-005b). We deliberately do
  // NOT filter the normal send path: a stale run still sitting in the queue has its newer sibling
  // ordered BEHIND it, so both depart in order and the server converges correctly — and dropping
  // it there would erase a legitimate transition the panel should show (e.g. error→ok delivered
  // in order, AC4). So the invariant lives at the one place a stale run can jump ahead of a newer
  // already-sent one: re-enqueue.
  const isFresh = run => lastStatus.get(run.id) === run.status;

  // Pulls the next batch off the FRONT of the queue, bounded by BOTH the server count cap
  // (RUNS_BATCH_LIMIT) and the keepalive byte cap (MAX_BODY_BYTES). Always takes at least one
  // run (a single run — error_message already char-capped — is far below MAX_BODY_BYTES), so
  // draining always makes progress.
  const takeChunk = () => {
    const chunk = [];
    let bytes = 0;
    while (runQueue.length && chunk.length < RUNS_BATCH_LIMIT) {
      const runBytes = byteLen(JSON.stringify(runQueue[0])) + 1; // +1 ≈ comma separator
      if (chunk.length && bytes + runBytes > MAX_BODY_BYTES) break;
      chunk.push(runQueue.shift());
      bytes += runBytes;
    }
    return chunk;
  };

  const postRuns = (runs, keepalive) =>
    fetchFn(runsUrl, {
      method: 'POST',
      keepalive,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ runs }),
    });

  // Exponential backoff with full jitter (AWS pattern): first send is immediate-debounce;
  // after N failures the retry waits random(0, min(CAP, delay × 2^N)) so recovering tabs
  // spread out instead of stampeding the server the moment it comes back.
  const nextDelay = () => {
    if (!flushFailures) return flushDelay;
    const ceiling = Math.min(
      FLUSH_BACKOFF_CAP_MS,
      flushDelay * 2 ** flushFailures
    );
    return Math.random() * ceiling;
  };

  // Normal (page-alive) send: SERIALIZED (single in-flight POST, REL-002), one count+byte-bounded
  // chunk at a time, with retry + backoff. keepalive:true is safe here because each chunk is byte-
  // bounded < 64KB, and it lets an in-flight POST survive a sudden unload (a gain over dropping
  // keepalive, which the browser would cancel on navigation). Aggregate keepalive budget is never
  // exceeded on this path because serialization keeps at most one in-flight request.
  const flushRuns = () => {
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    if (!runQueue.length || !fetchFn || inFlight) return;
    const runs = takeChunk();
    inFlight = true;

    const settle = confirmed => {
      inFlight = false;
      if (confirmed) {
        flushFailures = 0;
      } else {
        // Unconfirmed (network reject / non-2xx): re-queue at the FRONT so chronological order
        // is preserved vs outcomes recorded during the in-flight POST — the retry then carries
        // the still-current outcomes in order and the server converges to the newest. Runs whose
        // status was SUPERSEDED by a newer record() while this POST was in flight are dropped
        // (REL-005b, `isFresh`): re-inserting them at the front could regress the server past a
        // newer status a terminal drain already sent. A still-current outcome (e.g. a stuck error
        // that has not changed) stays `isFresh` and is retried, preserving REL-001.
        flushFailures += 1;
        runQueue.unshift(...runs.filter(isFresh));
      }
      // Remaining chunks (success) or the re-queued batch (failure) go out next.
      if (runQueue.length && !flushTimer)
        flushTimer = setTimeout(flushRuns, nextDelay());
    };

    try {
      // Fire-and-forget: a failed report must never surface to the host app.
      const sent = postRuns(runs, true);
      if (sent && typeof sent.then === 'function') {
        sent.then(
          response => settle(!!(response && response.ok)),
          () => settle(false)
        );
      } else {
        // Non-promise fetchFn (test injection): treat as delivered so flushFailures resets and
        // any remaining chunks are still scheduled (REL-NONPROMISE-004).
        settle(true);
      }
    } catch {
      // Synchronous fetch throw — retry on the next flush.
      settle(false);
    }
  };

  const scheduleFlush = () => {
    if (flushTimer) return; // debounce: coalesce a burst into a single delayed flush
    flushTimer = setTimeout(flushRuns, nextDelay());
  };

  // Terminal (unload / background) drain: the page may never run another timer, so send the WHOLE
  // queue NOW as sequential keepalive POSTs — each count+byte-bounded — instead of one chunk
  // (REL-004 / REL-VISIBILITY-002). No retry (dying page). Bounded by the browser's aggregate
  // keepalive budget: a queue larger than that loses its tail on a truly-dying page (unavoidable;
  // self-heals next session). A second terminal event (visibilitychange then pagehide on close)
  // naturally no-ops on the now-empty queue, so no explicit double-drain guard is needed. On a mere
  // tab-switch the page survives, so a budget-deferred tail is re-armed onto the normal debounced
  // flush before returning (REL-005a) instead of being left stuck until the next record().
  const drainTerminal = () => {
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    if (!fetchFn) return;
    let budget = TERMINAL_KEEPALIVE_BUDGET_BYTES;
    while (runQueue.length) {
      const chunk = takeChunk();
      const bytes = byteLen(JSON.stringify({ runs: chunk }));
      if (bytes > budget) {
        runQueue.unshift(...chunk); // keepalive budget spent — leave the rest queued
        break;
      }
      budget -= bytes;
      try {
        // keepalive fetch can reject ASYNC during unload (REL-005c) — swallow it so a failed
        // terminal report never escapes as an unhandled rejection into the host app. The sync
        // catch below still handles a synchronous fetch throw.
        const sent = postRuns(chunk, true);
        if (sent && typeof sent.catch === 'function') sent.catch(() => {});
      } catch {
        runQueue.unshift(...chunk);
        break;
      }
    }
    // REL-005a: if the aggregate keepalive budget left a tail queued, re-arm the debounce so a
    // page that SURVIVES the terminal signal (a tab-switch, or visibilitychange→hidden without an
    // actual unload) still drains it. On a truly-dying page this timer simply never fires (inert).
    if (runQueue.length) scheduleFlush();
  };

  // Enqueue a run outcome. Dedup + payload shaping live here because `lastStatus` is the
  // reporter's (now document-wide) state — a script that stays `ok` across portal navigations
  // is reported once, not once per navigation.
  const record = (script, status, error) => {
    // Without a report_token the endpoint would reject the report, so skip it entirely
    // (also keeps the 9.3/9.4 test payloads, which carry no token, from POSTing).
    if (script?.report_token == null) return;
    if (lastStatus.get(script.id) === status) return; // dedup by status change per id
    // Optimistic: set now so a status repeated every route is not re-queued (AC3a). Every
    // path that can DROP an outcome (queue cap below) rolls this back so a dropped status is
    // re-recorded later; the normal path re-queues on failure (REL-001), so the optimistic
    // entry never permanently suppresses a status (DEDUP-OPTIMISTIC-003).
    lastStatus.set(script.id, status);

    const run = {
      id: script.id,
      version: script.version,
      status,
      report_token: script.report_token,
    };
    if (status === 'error') {
      run.error_message = String(error?.message ?? error ?? '').slice(
        0,
        ERROR_MESSAGE_LIMIT
      );
    }
    runQueue.push(run);

    // Defensive cap (REL-QUEUE-GROWTH-006): drop the OLDEST outcome past the bound and roll back
    // its dedup entry (unless a newer queued entry for the same id still governs) so the status
    // is re-recorded on its next occurrence rather than being silently suppressed.
    while (runQueue.length > MAX_QUEUE) {
      const dropped = runQueue.shift();
      if (!runQueue.some(queued => queued.id === dropped.id)) {
        lastStatus.delete(dropped.id);
      }
    }

    scheduleFlush();
  };

  // pagehide AND visibilitychange→hidden are both treated as terminal signals: on mobile,
  // pagehide is unreliable and visibilitychange→hidden is the only dependable one, after which
  // timers freeze — so both must force-drain, not do a serialized best-effort flush.
  const onPageHide = () => drainTerminal();
  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') drainTerminal();
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibilityChange);
  }

  // Disposes the reporter: force-drain the whole queue (terminal semantics — the owner is going
  // away), THEN remove the document listeners. Called only when the OWNER disposes the reporter
  // (a per-instance reporter on engine teardown, or the document on unload) — never on a
  // Turbolinks navigation, where the shared reporter must survive.
  const teardown = () => {
    drainTerminal();
    // REL-005a re-arms the debounce when a budget-deferred tail remains, which is right for the
    // pagehide/visibilitychange paths (page survives → drain the tail later). But teardown is a
    // DISPOSE — the owner is going away — so that timer must NOT outlive it, or a disposed reporter
    // keeps firing keepalive POSTs after its listeners are gone. Cancel it here (the over-budget
    // tail is dropped, same best-effort as the terminal keepalive budget). Only the dispose path
    // clears it; the survive-page paths keep the reschedule.
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
  };

  return { record, flush: () => flushRuns(), teardown };
}

export function createEngine({
  scripts = [],
  surface,
  store,
  router,
  api,
  logger = defaultLogger,
  fetchFn = typeof window !== 'undefined' && window.fetch
    ? window.fetch.bind(window)
    : null,
  runsUrl = RUNS_URL,
  flushDelay = FLUSH_DELAY_MS,
  reporter = null,
} = {}) {
  // Buffer of internal engine errors (teardown/cleanup failures) — logged + kept for tests.
  // Distinct from run OUTCOMES, which are reported to the server via the run-reporter below.
  const errors = [];
  // Single source of truth for live instances, keyed by `script.id` → { teardown }.
  // Registry invariant: at most one live instance per id at any time (AC4).
  const instances = new Map();
  // Removal fn returned by `router.afterEach`; nulled after teardown (AC7).
  let removeAfterEach = null;

  const log = (...args) => logger.log(...args);
  const reportError = (...args) => {
    logger.error(...args);
    errors.push(args);
  };

  // Run-reporter (REL-003): document-scoped when a shared `reporter` is injected (portal, so
  // outcomes across engine instances share one serialized queue + dedup map), otherwise a
  // per-instance one is created for single-instance surfaces (dashboard/widget) — behaviour
  // there is identical since only one engine ever exists. We only tear DOWN a reporter we own.
  const ownsReporter = !reporter;
  const runReporter =
    reporter ?? createRunReporter({ fetchFn, runsUrl, flushDelay });
  const recordOutcome = (script, status, error) =>
    runReporter.record(script, status, error);

  const currentPath = () => router?.currentRoute?.value?.fullPath ?? '';

  // css → managed <style data-cw-script="ID"> in <head>, removed on teardown (EC18).
  // Returns the instance teardown.
  const runCss = script => {
    const style = document.createElement('style');
    style.setAttribute(CSS_ATTR, String(script.id));
    style.textContent = script.content || '';
    document.head.appendChild(style);
    recordOutcome(script, 'ok');
    return () => style.remove();
  };

  // javascript → own `async (ctx) => {}` sandbox. Sync throws and async rejections
  // are caught and reported; one failure never breaks other scripts or the app (EC7).
  // Returns the instance teardown (registered even if the body throws, so any DOM it
  // created before the throw is still released).
  // `isCurrent` (from startScript) is true only while this run is still the live instance
  // for its id. Async outcomes from a superseded/torn-down run are dropped so a slow result
  // from a previous route cannot overwrite the current run's reported status.
  const runJs = (script, isCurrent = () => true) => {
    const { ctx, teardown } = buildScriptContext({
      script,
      surface,
      store,
      router,
      api,
      log,
      reportError,
    });

    try {
      const fn = new AsyncFunction('ctx', script.content || '');
      const result = fn(ctx);
      if (result && typeof result.then === 'function') {
        result
          .then(cleanup => {
            // A returned cleanup fn is treated as one ctx.onCleanup (AC6).
            if (typeof cleanup === 'function') ctx.onCleanup(cleanup);
            if (isCurrent()) recordOutcome(script, 'ok');
          })
          .catch(error => {
            reportError(`script ${script.id} rejected`, error);
            if (isCurrent()) recordOutcome(script, 'error', error);
          });
      } else {
        // Synchronous outcome — always for the run being started right now.
        if (typeof result === 'function') ctx.onCleanup(result);
        recordOutcome(script, 'ok');
      }
    } catch (error) {
      reportError(`script ${script.id} threw`, error);
      recordOutcome(script, 'error', error);
    }

    return teardown;
  };

  // Tears down the live instance for `id` (if any) and drops it from the registry.
  const teardownInstance = id => {
    const instance = instances.get(id);
    if (!instance) return;
    instances.delete(id);
    try {
      instance.teardown();
    } catch (error) {
      reportError(`teardown failed for script ${id}`, error);
    }
  };

  // Single run + register path: tears down any prior instance for this id, runs the
  // script, and records the new instance (registry invariant).
  const startScript = script => {
    teardownInstance(script.id);
    // Identity token for this run: an async outcome is only reported while this exact
    // instance is still the live one for the id (guards the stale-overwrite race).
    const instance = { teardown: () => {} };
    const isCurrent = () => instances.get(script.id) === instance;
    try {
      const teardown =
        script.script_type === 'css'
          ? runCss(script)
          : runJs(script, isCurrent);
      instance.teardown = teardown;
      instances.set(script.id, instance);
    } catch (error) {
      reportError(`failed to run script ${script?.id}`, error);
    }
  };

  // Reconcile on SPA navigation (AC2). `load` instances are left untouched; each
  // `each_route` script is torn down and re-run ONLY when the new path matches its
  // `url_pattern` (blank pattern = always). Exported so tests can drive it directly.
  const applyRoute = fullPath => {
    scripts.forEach(script => {
      if (script.run_on !== 'each_route') return;
      teardownInstance(script.id);
      if (matchUrlPattern(script.url_pattern, fullPath)) startScript(script);
    });
  };

  const boot = () => {
    const path = currentPath();
    // afterEach does NOT fire for the already-resolved initial route, so boot() must
    // run the current route explicitly (both `load` and matching `each_route`).
    scripts.forEach(script => {
      if (matchUrlPattern(script.url_pattern, path)) startScript(script);
    });
    removeAfterEach = router?.afterEach(to => applyRoute(to.fullPath)) ?? null;
    // Run-reporter page-lifecycle flushing (pagehide keepalive + visibilitychange) is owned by
    // the reporter itself (REL-003), so boot no longer wires those listeners — this keeps a
    // shared reporter alive across Turbolinks navigations that recreate the engine.
  };

  // Removes every tracked node/listener/observer and unregisters the afterEach hook
  // (AC7). Idempotent: a second call is a no-op (registry drained, hook already
  // removed), so re-run/post-teardown navigation leaves no duplicates (EC2).
  const teardown = () => {
    if (removeAfterEach) {
      removeAfterEach();
      removeAfterEach = null;
    }
    // Only dispose a reporter we created (single-instance surfaces). A shared/injected reporter
    // MUST survive engine teardown — on the portal the next Turbolinks navigation recreates the
    // engine but reuses the same reporter, so tearing it down here would resurrect the
    // cross-instance stale-write window REL-003 closes.
    if (ownsReporter) runReporter.teardown();
    instances.forEach((instance, id) => {
      try {
        instance.teardown();
      } catch (error) {
        reportError(`teardown failed for script ${id}`, error);
      }
    });
    instances.clear();
  };

  // `flushRuns` is exposed for tests/back-compat; it delegates to the reporter's serialized flush.
  return {
    boot,
    teardown,
    applyRoute,
    flushRuns: () => runReporter.flush(),
    reporter: runReporter,
    instances,
    errors,
  };
}

// Convenience boot used by the dashboard entrypoint: reads the island and runs.
// `reporter` is optional — the portal injects one shared instance across navigations (REL-003);
// dashboard/widget omit it and each engine gets its own (single-instance, so equivalent).
export function bootCustomScripts({
  surface,
  store,
  router,
  api,
  doc = document,
  logger = defaultLogger,
  reporter = null,
} = {}) {
  const scripts = readIsland(doc, logger);
  const engine = createEngine({
    scripts,
    surface,
    store,
    router,
    api,
    logger,
    reporter,
  });
  engine.boot();
  return engine;
}
